import { getVehicleChassisById } from "@/data/vehicles";
import { VEHICLE_WEAPONS } from "@/data/vehicles";
import type { ForceVehicle } from "@/types/force";

interface VehicleStatCardProps {
	vehicle: ForceVehicle;
}

export function VehicleStatCard({ vehicle }: VehicleStatCardProps) {
	const chassis = getVehicleChassisById(vehicle.chassisId);
	if (!chassis) return null;

	// Collect all weapons: included + mounted
	const includedWeaponDefs = (chassis.includedWeapons ?? [])
		.map((id) => VEHICLE_WEAPONS.find((vw) => vw.id === id))
		.filter(Boolean);

	const mountedWeaponDefs = vehicle.mountSelections
		.filter((ms) => ms.weaponId)
		.map((ms) => VEHICLE_WEAPONS.find((vw) => vw.id === ms.weaponId))
		.filter(Boolean);

	const allWeapons = [...includedWeaponDefs, ...mountedWeaponDefs];
	const weaponsWithStats = allWeapons.filter((w) => w!.stats);
	const weaponsWithoutStats = allWeapons.filter((w) => !w!.stats);

	return (
		<div className="print-stat-card border border-gray-400 rounded-lg p-3 mb-3 bg-white break-inside-avoid">
			{/* Header */}
			<div className="flex items-center justify-between border-b border-gray-300 pb-2 mb-2">
				<div>
					<span className="font-bold text-sm text-gray-900">
						{vehicle.customName || chassis.name}
					</span>
					<span className="text-xs text-gray-500 ml-2">({chassis.category})</span>
				</div>
				<span className="text-xs font-mono font-bold text-gray-700">{chassis.baseCost}r</span>
			</div>

			{/* Vehicle Stats */}
			<div className="flex gap-2 mb-2">
				{[
					{ label: "M", value: chassis.movement != null ? `${chassis.movement}"` : "-" },
					{ label: "Class", value: chassis.vehicleClass ?? "-" },
					{ label: "AV", value: chassis.armourValue ?? "-" },
					{ label: "T", value: chassis.transport ?? "-" },
				].map((stat) => (
					<div
						key={stat.label}
						className="flex flex-col items-center border border-gray-300 rounded px-2 py-1 min-w-[2.5rem]"
					>
						<span className="text-[10px] font-bold text-gray-500 uppercase">{stat.label}</span>
						<span className="text-sm font-bold text-gray-900">{stat.value}</span>
					</div>
				))}
			</div>

			{/* Special Rules */}
			{chassis.vehicleSpecialRules && chassis.vehicleSpecialRules.length > 0 && (
				<div className="mb-2 text-[10px]">
					<span className="font-bold text-gray-600">Special: </span>
					<span className="text-gray-700">{chassis.vehicleSpecialRules.join(", ")}</span>
				</div>
			)}

			{/* Weapon Table */}
			{weaponsWithStats.length > 0 && (
				<div className="mb-2 overflow-x-auto">
					<table className="w-full text-[10px] border-collapse">
						<thead>
							<tr className="bg-gray-100">
								<th className="text-left px-1 py-0.5 border border-gray-300 font-bold">Weapon</th>
								<th colSpan={4} className="text-center px-1 py-0.5 border border-gray-300 font-bold">CQB</th>
								<th colSpan={4} className="text-center px-1 py-0.5 border border-gray-300 font-bold">Engagement</th>
								<th colSpan={4} className="text-center px-1 py-0.5 border border-gray-300 font-bold">Extreme</th>
								<th className="text-left px-1 py-0.5 border border-gray-300 font-bold">Special</th>
							</tr>
							<tr className="bg-gray-50">
								<th className="border border-gray-300 px-1"></th>
								{["R", "M", "L", "AP"].map((h) => (
									<th key={`cqb-${h}`} className="text-center px-0.5 border border-gray-300 font-medium text-gray-600">{h}</th>
								))}
								{["R", "M", "L", "AP"].map((h) => (
									<th key={`eng-${h}`} className="text-center px-0.5 border border-gray-300 font-medium text-gray-600">{h}</th>
								))}
								{["R", "M", "L", "AP"].map((h) => (
									<th key={`ext-${h}`} className="text-center px-0.5 border border-gray-300 font-medium text-gray-600">{h}</th>
								))}
								<th className="border border-gray-300 px-1"></th>
							</tr>
						</thead>
						<tbody>
							{weaponsWithStats.map((w, idx) => {
								const s = w!.stats!;
								return (
									<tr key={`${w!.id}-${idx}`}>
										<td className="px-1 py-0.5 border border-gray-300 font-medium text-gray-900 whitespace-nowrap">{w!.name}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.cqb.range}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.cqb.modifier != null ? (s.cqb.modifier >= 0 ? `+${s.cqb.modifier}` : s.cqb.modifier) : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.cqb.lethality ?? "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.cqb.ap != null ? s.cqb.ap : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.engagement.range}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.engagement.modifier != null ? (s.engagement.modifier >= 0 ? `+${s.engagement.modifier}` : s.engagement.modifier) : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.engagement.lethality ?? "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.engagement.ap != null ? s.engagement.ap : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.extreme.range}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.extreme.modifier != null ? (s.extreme.modifier >= 0 ? `+${s.extreme.modifier}` : s.extreme.modifier) : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.extreme.lethality ?? "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.extreme.ap != null ? s.extreme.ap : "-"}</td>
										<td className="px-1 py-0.5 border border-gray-300 text-gray-700">{s.specialRules.join(", ") || "-"}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* Weapons without stat profiles */}
			{weaponsWithoutStats.length > 0 && (
				<div className="mb-1 text-[10px] text-gray-600">
					<span className="font-bold">Other Weapons: </span>
					{weaponsWithoutStats.map((w) => w!.name).join(", ")}
				</div>
			)}

			{/* Notes */}
			{chassis.notes && (
				<div className="text-[10px] text-gray-500 italic border-t border-gray-200 pt-1 mt-1">
					{chassis.notes}
				</div>
			)}
		</div>
	);
}
